/**
 * EmailManager — three TAD-aligned movements on the Orchist's email body.
 *
 * Email Fluidity Doctrine (sacred/free-will-and-fluidity.md):
 *   The Orchist's body of contact-points is fluid. Connections form;
 *   connections dissolve. The Line keeps the trail.
 *
 * Three Movements (B7, B8, B9):
 *
 *   1. ADD a candidate connection
 *      User types an email; it joins ContactEmail as isPrimary=false,
 *      verifiedAt=null. The Line knows about it. The current anchor
 *      stays untouched. No Supabase call yet — this is a candidate, a
 *      relationship-in-formation. Verification of secondaries (without
 *      auth.updateUser) is Phase J (custom Edge Function).
 *
 *   2. SWAP the anchor (promote a candidate)
 *      A SEPARATE deliberate gesture. User picks an existing row and
 *      clicks "Set as anchor." The client calls
 *      supabase.auth.updateUser({ email }) which triggers Supabase's
 *      attestation. The candidate IS the verification — the inbox click
 *      proves ownership AND completes the swap. The old anchor moves to
 *      history.
 *
 *   3. DISCONNECT (release a connection)
 *      A THIRD separate gesture. Setting disconnectedAt=now on a
 *      non-anchor row. Cannot disconnect the anchor (floor: at least
 *      one connection). The body of memory holds the trail.
 *
 * JL feedback 2026-04-26 eve:
 *   "why directly swap? Can't we just add emails first in the profile's
 *    list? Then whatever the current anchor, stays? Until the Orchist
 *    decides when to swap anchor? Also, aside from that, TOTALLY SEPARATE
 *    process is, removing and releasing email from profile? EVERYTHING TAD."
 *
 * Doctrinally implemented as three movements in this file.
 */

import { useEffect, useState, type FormEvent, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  useContactEmails,
  splitEmailsByConnection,
  type ContactEmailRow,
} from '@/hooks/useContactEmails';
import { useEmailShares } from '@/hooks/useEmailShares';
import { useLineage, lineageDisplayString } from '@/hooks/useLineage';
import { useNicknames } from '@/hooks/useNicknames';
import { usePendingInvitees } from '@/hooks/usePendingInvitees';
import { useAnchorAttestation } from '@/hooks/useAnchorAttestation';
import { utcToLocalDateString, resolveTimezone } from '@/lib/dates';

export interface EmailManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Translate Supabase auth errors into doctrine-aligned copy. */
function translateAuthError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '');
  const lower = raw.toLowerCase();
  if (lower.includes('already been registered') || lower.includes('already registered')) {
    return 'That email is already attached to a separate auth identity. The Line cannot bind it to two records at once. The other identity must release the email first (see "Release my email" if you signed up there by accident), or pick a different address.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Too many recent attempts. Wait a moment, then try again.';
  }
  if (lower.includes('expired')) {
    return 'The confirmation link has expired. Resend, or propose a different address.';
  }
  if (lower.includes('invalid email') || lower.includes('valid email')) {
    return 'That does not look like a valid email address. Check the spelling and try again.';
  }
  if (lower.includes('already in use')) {
    return 'That email is already on your record (or someone else holds it). Pick another, or check your existing connections.';
  }
  return raw || 'Something faltered. Try again, or pick a different address.';
}

export function EmailManager({ isOpen, onClose }: EmailManagerProps) {
  const { contact, session } = useAuth();
  const { emails, loading, refresh } = useContactEmails();
  const viewerTz = resolveTimezone(contact?.timezone);

  // Pending swap from Supabase (auth.users.email_change set, awaiting click)
  const pendingEmail = (session?.user as { new_email?: string } | undefined)?.new_email ?? null;

  // Phase H attestation row — runs in parallel with Supabase's flow.
  // Records the gesture in our body of memory; the actual auth swap is
  // still Supabase's domain. The rich pending banner displays both states.
  const {
    active: anchorAttestation,
    propose: proposeAnchorChange,
    cancel: cancelAnchorAttestation,
    refresh: refreshAttestation,
  } = useAnchorAttestation();

  // Movement 1: Add candidate
  const [newEmail, setNewEmail] = useState('');
  const [addState, setAddState] = useState<'idle' | 'submitting' | 'added' | 'error'>('idle');
  const [addError, setAddError] = useState<string | null>(null);

  // Movement 2: Swap anchor (promote a candidate)
  const [swapState, setSwapState] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapTarget, setSwapTarget] = useState<string | null>(null); // email being swapped TO

  // Movement 3: Disconnect
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  // Movement 4 (Phase J): Verify a candidate independently of Swap
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyState, setVerifyState] = useState<Record<string, 'sent' | 'error'>>({});
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Resend on pending swap
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setNewEmail('');
      setAddState('idle');
      setAddError(null);
      setSwapState('idle');
      setSwapError(null);
      setSwapTarget(null);
      setDisconnectingId(null);
      setDisconnectError(null);
      setResendState('idle');
      setResendError(null);
      setHistoryOpen(false);
      setVerifyingId(null);
      setVerifyState({});
      setVerifyError(null);

      // Force a server-side session refresh so session.user.new_email
      // reflects the truth — not the stale value cached in this browser
      // tab. Common case: swap completes via inbox click in another tab,
      // this tab's session.new_email never clears, EmailManager keeps
      // showing "Awaiting confirmation" forever. refreshSession() pulls
      // the latest user object from auth.users.
      void supabase.auth.refreshSession();
      // Phase H: also refresh the attestation state so the rich banner
      // reflects any cross-tab attestation events.
      refreshAttestation();
    }
  }, [isOpen, refreshAttestation]);

  if (!isOpen) return null;

  const { active, history } = splitEmailsByConnection(emails);
  const anchor = active.find((e) => e.isPrimary) ?? null;
  const candidates = active.filter((e) => !e.isPrimary);
  const isSole = active.length === 1;

  // -------------------------------------------------------------------------
  // Movement 1: ADD candidate
  // -------------------------------------------------------------------------
  async function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (addState === 'submitting') return;
    const target = newEmail.trim();
    if (!target || !target.includes('@') || target.length < 5) {
      setAddError('Type a valid email address.');
      return;
    }
    if (anchor && target.toLowerCase() === anchor.email.toLowerCase()) {
      setAddError('That is already your anchor.');
      return;
    }
    if (candidates.some((c) => c.email.toLowerCase() === target.toLowerCase())) {
      setAddError('Already on your candidate list.');
      return;
    }
    setAddState('submitting');
    setAddError(null);
    try {
      const { error } = await supabase.schema('core').rpc('add_contact_email', {
        p_email: target,
      });
      if (error) throw new Error(error.message);
      setAddState('added');
      setNewEmail('');
      refresh();
    } catch (err) {
      setAddState('error');
      setAddError(translateAuthError(err));
    }
  }

  // -------------------------------------------------------------------------
  // Movement 2: SWAP anchor (promote a candidate)
  // -------------------------------------------------------------------------
  async function handleSwap(candidate: ContactEmailRow) {
    if (swapState === 'submitting') return;
    setSwapState('submitting');
    setSwapTarget(candidate.email);
    setSwapError(null);

    let resolved = false;
    const optimisticTimer = window.setTimeout(() => {
      if (resolved) return;
      setSwapState((s) => (s === 'submitting' ? 'sent' : s));
    }, 6000);

    try {
      // Validate candidate first — fast feedback if something's off
      const { error: validateErr } = await supabase
        .schema('core')
        .rpc('validate_anchor_candidate', { p_email_id: candidate.id });
      if (validateErr) throw new Error(validateErr.message);

      // Trigger Supabase's attestation. The intent param survives Supabase's
      // redirect verbatim so AuthCallback knows this is an anchor swap.
      const { error } = await supabase.auth.updateUser({
        email: candidate.email,
        emailRedirectTo: `${window.location.origin}/auth/callback?intent=email-change`,
      } as { email: string; emailRedirectTo?: string });
      resolved = true;
      window.clearTimeout(optimisticTimer);
      if (error) throw new Error(error.message);

      // Phase H: also record the gesture in our body of memory. Failures
      // here are non-fatal — the Supabase flow above is the actual binding
      // mechanism; Phase H is the audit trail value-add. If propose fails
      // (e.g., already-active attestation row exists from a prior aborted
      // attempt), surface the warning silently to the console + carry on.
      try {
        await proposeAnchorChange(candidate.email);
      } catch (phaseHErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Phase H] propose_anchor_change failed (non-fatal):',
          phaseHErr instanceof Error ? phaseHErr.message : phaseHErr
        );
      }

      setSwapState('sent');
    } catch (err) {
      resolved = true;
      window.clearTimeout(optimisticTimer);
      setSwapState('error');
      setSwapError(translateAuthError(err));
    }
  }

  // -------------------------------------------------------------------------
  // Phase H: cancel the active attestation. Note: this only cancels OUR
  // gesture record. Supabase's parallel flow may still be pending — to
  // fully cancel that, the Orchist must NOT click Supabase's confirmation
  // link from either inbox. (Supabase auto-expires its pending change
  // after about an hour.)
  // -------------------------------------------------------------------------
  async function handleCancelPhaseH() {
    try {
      await cancelAnchorAttestation();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Phase H] cancel_anchor_change failed:',
        err instanceof Error ? err.message : err
      );
    }
  }

  // -------------------------------------------------------------------------
  // Movement 3: DISCONNECT
  // -------------------------------------------------------------------------
  async function handleDisconnect(row: ContactEmailRow) {
    if (disconnectingId) return;
    if (row.isPrimary) {
      setDisconnectError('Cannot disconnect the anchor. Set another email as anchor first.');
      return;
    }
    setDisconnectingId(row.id);
    setDisconnectError(null);
    try {
      const { error } = await supabase
        .schema('core')
        .rpc('disconnect_contact_email', { p_email_id: row.id, p_reason: null });
      if (error) throw new Error(error.message);
      setDisconnectingId(null);
      refresh();
    } catch (err) {
      setDisconnectingId(null);
      setDisconnectError(translateAuthError(err));
    }
  }

  // -------------------------------------------------------------------------
  // Movement 4 (Phase J): VERIFY a candidate independently of Swap
  // -------------------------------------------------------------------------
  async function handleVerify(row: ContactEmailRow) {
    if (verifyingId) return;
    setVerifyingId(row.id);
    setVerifyError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        'send-email-verification',
        { body: { emailId: row.id } }
      );
      if (error) throw new Error(error.message);
      const result = data as { sent?: boolean; error?: string };
      if (result?.error) throw new Error(result.error);
      if (!result?.sent) throw new Error('Verification was not sent.');
      setVerifyState((s) => ({ ...s, [row.id]: 'sent' }));
      setVerifyingId(null);
      // Refresh so verificationSentAt + tokenExpiresAt show up
      refresh();
    } catch (err) {
      setVerifyingId(null);
      setVerifyState((s) => ({ ...s, [row.id]: 'error' }));
      setVerifyError(translateAuthError(err));
    }
  }

  // -------------------------------------------------------------------------
  // Resend pending swap confirmation
  // -------------------------------------------------------------------------
  async function handleResend() {
    if (!pendingEmail || resendState === 'sending') return;
    setResendState('sending');
    setResendError(null);
    let resolved = false;
    const optimisticTimer = window.setTimeout(() => {
      if (resolved) return;
      setResendState((s) => (s === 'sending' ? 'sent' : s));
    }, 6000);
    try {
      const { error } = await supabase.auth.updateUser({
        email: pendingEmail,
        emailRedirectTo: `${window.location.origin}/auth/callback?intent=email-change`,
      } as { email: string; emailRedirectTo?: string });
      resolved = true;
      window.clearTimeout(optimisticTimer);
      if (error) throw new Error(error.message);
      setResendState('sent');
    } catch (err) {
      resolved = true;
      window.clearTimeout(optimisticTimer);
      setResendState('error');
      setResendError(translateAuthError(err));
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close email manager"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-lg bg-paper border border-ink/20 shadow-xl p-8 space-y-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The fluid body of contact
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">Connections.</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
            Three movements on the body of email: add a candidate, set the
            anchor, release a connection. The Line keeps the trail of all of
            them.
          </p>
        </div>

        {/* Pending swap banner. Two layered states render together when
            both fire simultaneously: Supabase's pending email_change
            (session.user.new_email) AND Phase H's EmailChangeAttestation
            row. They convey overlapping but distinct info. */}
        {(pendingEmail || anchorAttestation) && (
          <div className="border border-accent/40 bg-accent/5 px-4 py-4 space-y-3">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-accent">
              Awaiting confirmation
            </p>
            {pendingEmail && (
              <p className="font-mono text-sm break-all">{pendingEmail}</p>
            )}
            {!pendingEmail && anchorAttestation && (
              <p className="font-mono text-sm break-all">{anchorAttestation.newEmail}</p>
            )}
            <p className="text-xs text-ink/55 italic font-display leading-relaxed">
              The anchor swap is a two-sided binding. Both the proposed
              address AND the current anchor each receive a link. <strong className="not-italic">Click both</strong> to
              complete the swap. Until both have attested, the current
              anchor stays active.
            </p>

            {/* Phase H rich state — renders TAD-honest event timeline */}
            {anchorAttestation && (
              <div className="border-l-2 border-accent/40 pl-3 py-1 space-y-1">
                <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/50">
                  The body of memory
                </p>
                <p className="text-xs italic font-display text-ink/65">
                  Old anchor:{' '}
                  {anchorAttestation.oldAttestedAt ? (
                    <span className="not-italic text-accent">
                      ✓ attested {utcToLocalDateString(anchorAttestation.oldAttestedAt, viewerTz) ?? '—'}
                    </span>
                  ) : (
                    <span className="not-italic text-ink/40">⏳ awaiting</span>
                  )}
                </p>
                <p className="text-xs italic font-display text-ink/65">
                  New anchor:{' '}
                  {anchorAttestation.newAttestedAt ? (
                    <span className="not-italic text-accent">
                      ✓ attested {utcToLocalDateString(anchorAttestation.newAttestedAt, viewerTz) ?? '—'}
                    </span>
                  ) : (
                    <span className="not-italic text-ink/40">⏳ awaiting</span>
                  )}
                </p>
                <p className="text-[0.65rem] text-ink/45 italic font-display">
                  Expires {utcToLocalDateString(anchorAttestation.expiresAt, viewerTz) ?? '—'}.
                  Status: <span className="font-mono">{anchorAttestation.status}</span>
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendState === 'sending' || resendState === 'sent'}
                className="text-xs italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/70 hover:text-ink transition-colors disabled:opacity-50"
              >
                {resendState === 'sending'
                  ? 'Resending...'
                  : resendState === 'sent'
                    ? 'Fresh link sent ✓'
                    : 'Resend confirmation'}
              </button>
              {anchorAttestation && (
                <>
                  <span className="text-ink/25 text-xs">·</span>
                  <button
                    type="button"
                    onClick={handleCancelPhaseH}
                    className="text-xs italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors"
                    title="Cancel the attestation in the body of memory. The gate's parallel pending change auto-expires after about an hour."
                  >
                    Cancel attestation
                  </button>
                </>
              )}
            </div>
            {resendError && (
              <p className="text-xs text-destructive italic" role="alert">
                {resendError}
              </p>
            )}
          </div>
        )}

        {/* Anchor section */}
        <section className="space-y-3">
          <p className="text-xs tracking-wide uppercase text-ink/60">
            {isSole ? 'The anchor' : 'Currently anchored'}
          </p>
          {loading ? (
            <p className="text-sm italic text-ink/40 font-display">Gathering...</p>
          ) : !anchor ? (
            <p className="text-sm italic text-ink/40 font-display">No anchor.</p>
          ) : (
            <div className="border border-ink/15 px-4 py-3">
              <p className="font-mono text-sm truncate">{anchor.email}</p>
              <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/40 mt-0.5">
                {isSole ? 'anchor · the sole connection' : 'anchor · the auth seat'}
                {anchor.verifiedAt && (
                  <span className="ml-2 text-ink/35 lowercase tracking-wider">
                    · verified
                  </span>
                )}
              </p>
            </div>
          )}
        </section>

        {/* Candidates section — secondaries waiting to potentially become anchor */}
        {candidates.length > 0 && (
          <section className="space-y-3">
            <p className="text-xs tracking-wide uppercase text-ink/60">
              Other connections
            </p>
            <ul className="space-y-2 list-none pl-0">
              {candidates.map((row) => {
                const verifyPending = !!row.verifyTokenHash && !row.verifiedAt;
                const verifyJustSent = verifyState[row.id] === 'sent';
                const isVerified = !!row.verifiedAt;
                return (
                  <li
                    key={row.id}
                    className="border border-ink/15 px-4 py-3 space-y-2"
                  >
                    <p className="font-mono text-sm truncate">{row.email}</p>
                    <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/40">
                      candidate
                      {isVerified
                        ? ' · verified'
                        : verifyPending || verifyJustSent
                          ? ' · verification sent (awaiting click)'
                          : ' · awaiting verification'}
                    </p>
                    <div className="flex flex-wrap gap-3 pt-1 text-xs">
                      {!isVerified && (
                        <button
                          type="button"
                          onClick={() => handleVerify(row)}
                          disabled={verifyingId === row.id || verifyJustSent}
                          className="italic underline underline-offset-4 decoration-ink/30 hover:decoration-ink/70 text-ink/70 hover:text-ink transition-colors disabled:opacity-40"
                          title="Send a verification email so the holder can attest the connection without changing the anchor"
                        >
                          {verifyingId === row.id
                            ? 'Sending...'
                            : verifyJustSent
                              ? 'Verification sent ✓'
                              : verifyPending
                                ? 'Resend verification'
                                : 'Send verification'}
                        </button>
                      )}
                      {!isVerified && <span className="text-ink/25">·</span>}
                      <button
                        type="button"
                        onClick={() => handleSwap(row)}
                        disabled={swapState === 'submitting' || !!pendingEmail}
                        className="italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/70 hover:text-ink transition-colors disabled:opacity-40"
                        title={
                          pendingEmail
                            ? 'A swap is already pending; resolve that first'
                            : 'Promote this email to anchor; the gate will send confirmation links'
                        }
                      >
                        {swapState === 'submitting' && swapTarget === row.email
                          ? 'Sending confirmation...'
                          : 'Set as anchor'}
                      </button>
                      <span className="text-ink/25">·</span>
                      <button
                        type="button"
                        onClick={() => handleDisconnect(row)}
                        disabled={disconnectingId === row.id}
                        className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors disabled:opacity-40"
                      >
                        {disconnectingId === row.id ? 'Releasing...' : 'Disconnect'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {(swapState === 'sent' || swapError || disconnectError || verifyError) && (
          <div className="space-y-1 text-center">
            {swapState === 'sent' && (
              <p className="text-sm text-accent italic font-display">
                Confirmation sent to {swapTarget}. Click the link there
                (and from the current anchor's inbox if both sides need
                to attest) to complete the swap.
              </p>
            )}
            {swapError && (
              <p className="text-sm text-destructive" role="alert">
                {swapError}
              </p>
            )}
            {disconnectError && (
              <p className="text-sm text-destructive" role="alert">
                {disconnectError}
              </p>
            )}
            {verifyError && (
              <p className="text-sm text-destructive" role="alert">
                {verifyError}
              </p>
            )}
          </div>
        )}

        {/* Add candidate form */}
        <form onSubmit={handleAdd} className="space-y-2 border-t border-ink/10 pt-6">
          <label
            htmlFor="add-email"
            className="block text-xs tracking-wide uppercase text-ink/60"
          >
            Add a candidate connection
          </label>
          <div className="flex gap-2">
            <input
              id="add-email"
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (addState === 'added' || addState === 'error') setAddState('idle');
                setAddError(null);
              }}
              disabled={addState === 'submitting'}
              placeholder="another@email.com"
              maxLength={255}
              className="flex-1 px-4 py-3 border border-ink/20 bg-paper/50 text-sm font-mono placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={addState === 'submitting' || !newEmail.trim()}
              className="px-5 py-3 border border-ink/30 text-ink/80 font-display tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {addState === 'submitting' ? 'Adding...' : 'Add'}
            </button>
          </div>
          <p className="text-[0.7rem] text-ink/40 italic font-display leading-relaxed">
            Adding a candidate is a claim. Click "Send verification" to
            ask the email holder to attest (the candidate becomes verified
            without changing your anchor). Or click "Set as anchor" later
            to promote it &mdash; that's when the gate asks both addresses
            to confirm AND completes the swap in one act.
          </p>
          {addState === 'added' && (
            <p className="text-xs text-accent italic font-display">
              Candidate added. Use "Set as anchor" when you're ready to
              move The Line's recognition to it.
            </p>
          )}
          {addError && (
            <p className="text-xs text-destructive italic" role="alert">
              {addError}
            </p>
          )}
        </form>

        {/* History */}
        {history.length > 0 && (
          <section className="border-t border-ink/10 pt-5">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="w-full text-left text-xs tracking-wide uppercase text-ink/45 hover:text-ink/70 transition-colors flex items-center justify-between"
            >
              <span>Previously connected ({history.length})</span>
              <span className="text-[0.65rem]">{historyOpen ? '— hide' : '+ show'}</span>
            </button>
            {historyOpen && (
              <>
                <p className="text-xs italic font-display text-ink/45 mt-3 mb-3 leading-relaxed">
                  The body of memory holds every connection that ever was.
                </p>
                <ul className="space-y-2 list-none pl-0">
                  {history.map((row) => (
                    <li key={row.id} className="border border-ink/10 px-4 py-2 opacity-60">
                      <p className="font-mono text-xs truncate">{row.email}</p>
                      <p className="text-[0.65rem] text-ink/45 italic font-display mt-0.5">
                        Disconnected{' '}
                        {utcToLocalDateString(row.disconnectedAt, viewerTz) ?? '—'}
                        {row.disconnectedReason && ` · ${row.disconnectedReason}`}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}

        {/* Email Sharing — per-relationship visibility */}
        <EmailSharingSection currentEmail={anchor?.email ?? null} viewerTz={viewerTz} />

        <button
          type="button"
          onClick={() => {
            refresh();
            onClose();
          }}
          className="w-full px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmailSharingSection — per-relationship visibility (Phase G.7.9)
// Owner-side surface: lists who currently has my email; allows revoke +
// bulk re-share with my CURRENT anchor.
// ---------------------------------------------------------------------------

function EmailSharingSection({
  currentEmail,
  viewerTz,
}: {
  currentEmail: string | null;
  viewerTz: string;
}) {
  const {
    iShared,
    byOwnerContactId,
    revokeShare,
    shareWith,
    shareWithAllRecognized,
  } = useEmailShares();
  const { rows: lineageRows } = useLineage();
  const { byTargetId: nicknamesByTargetId } = useNicknames();
  const { rows: pendingInvitees } = usePendingInvitees();

  const [open, setOpen] = useState(false);
  const [actionState, setActionState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const targetById = useMemo(() => {
    const map = new Map<string, (typeof lineageRows)[number]>();
    for (const r of lineageRows) map.set(r.id, r);
    return map;
  }, [lineageRows]);

  // Pending invitees may appear as viewers on shares created before they
  // Acknowledged. Surface their email + a "(pending acknowledgment)"
  // badge instead of the bald "(unknown Orchist)" fallback.
  const pendingByContactId = useMemo(() => {
    const m = new Map<string, (typeof pendingInvitees)[number]>();
    for (const p of pendingInvitees) m.set(p.id, p);
    return m;
  }, [pendingInvitees]);

  // Toggle UX: show ALL shares (active + previously-revoked) so the user can
  // flip them back on. Sort active first, then by sharedAt desc.
  const allShares = useMemo(() => {
    return [...iShared].sort((a, b) => {
      if (!a.revokedAt && b.revokedAt) return -1;
      if (a.revokedAt && !b.revokedAt) return 1;
      return (b.sharedAt ?? '').localeCompare(a.sharedAt ?? '');
    });
  }, [iShared]);

  const activeCount = iShared.filter((s) => !s.revokedAt).length;

  // Stale-share detection on active subset only
  const staleCount = currentEmail
    ? iShared.filter((s) => !s.revokedAt && s.sharedEmail.toLowerCase() !== currentEmail.toLowerCase()).length
    : 0;

  async function handleToggle(viewerContactId: string, currentlyActive: boolean) {
    if (actionState === 'busy') return;
    setActionState('busy');
    setErrorMessage(null);
    setFeedback(null);
    try {
      if (currentlyActive) {
        await revokeShare(viewerContactId, null);
        setFeedback('Hidden from this Orchist.');
      } else {
        await shareWith(viewerContactId);
        setFeedback('Shown to this Orchist.');
      }
      setActionState('idle');
    } catch (err) {
      setActionState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not toggle.');
    }
  }

  async function handleReshare(viewerContactId: string) {
    if (actionState === 'busy') return;
    setActionState('busy');
    setErrorMessage(null);
    setFeedback(null);
    try {
      await shareWith(viewerContactId);
      setActionState('idle');
      setFeedback('Shared with current anchor.');
    } catch (err) {
      setActionState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not share.');
    }
  }

  async function handleBulkShare() {
    if (actionState === 'busy') return;
    setActionState('busy');
    setErrorMessage(null);
    setFeedback(null);
    try {
      const result = (await shareWithAllRecognized()) as {
        sharedWithCount: number;
        skippedPending?: number;
        skippedAnonymous?: number;
        currentEmail: string;
      };
      setActionState('idle');
      const parts: string[] = [
        `Shared with ${result.sharedWithCount} recognized Orchist${result.sharedWithCount === 1 ? '' : 's'}.`,
      ];
      if (result.skippedPending && result.skippedPending > 0) {
        parts.push(
          `Skipped ${result.skippedPending} pending invitation${result.skippedPending === 1 ? '' : 's'} (not yet Acknowledged).`
        );
      }
      if (result.skippedAnonymous && result.skippedAnonymous > 0) {
        parts.push(
          `Skipped ${result.skippedAnonymous} anonymously-cloaked relationship${result.skippedAnonymous === 1 ? '' : 's'} — share individually to reveal.`
        );
      }
      setFeedback(parts.join(' '));
    } catch (err) {
      setActionState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not bulk-share.');
    }
  }

  return (
    <section className="border-t border-ink/10 pt-6 space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left text-xs tracking-wide uppercase text-ink/60 hover:text-ink/80 transition-colors flex items-center justify-between"
      >
        <span>
          Email visibility ({activeCount} showing
          {staleCount > 0 && (
            <span className="text-accent normal-case lowercase tracking-normal italic ml-2">
              · {staleCount} frozen at older anchor
            </span>
          )})
        </span>
        <span className="text-[0.65rem]">{open ? '— hide' : '+ show'}</span>
      </button>

      {open && (
        <>
          <p className="text-xs italic font-display text-ink/45 leading-relaxed">
            Each share is frozen at the email that was current when you shared
            it. Anchor swaps don't auto-update past shares — you re-share
            (or revoke) to control visibility per relationship.
          </p>

          <div className="flex flex-wrap gap-3 text-xs">
            <button
              type="button"
              onClick={handleBulkShare}
              disabled={actionState === 'busy' || !currentEmail}
              className="px-4 py-2 border border-ink/30 text-ink/75 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
              title="Share my CURRENT anchor email with everyone I have a relationship with (my inviter + invitees)"
            >
              Share current with all recognized
            </button>
          </div>

          {feedback && (
            <p className="text-xs italic text-accent font-display">{feedback}</p>
          )}
          {errorMessage && (
            <p className="text-xs text-destructive italic" role="alert">{errorMessage}</p>
          )}

          {allShares.length === 0 ? (
            <p className="text-sm italic text-ink/40 font-display">
              You haven't shared your email with anyone yet.
            </p>
          ) : (
            <ul className="space-y-2 list-none pl-0">
              {allShares.map((s) => {
                const target = targetById.get(s.viewerContactId);
                const pending = pendingByContactId.get(s.viewerContactId);
                const isPending = !target && !!pending;
                const display = target
                  ? lineageDisplayString(target, viewerTz)
                  : pending
                    ? pending.email
                    : '(unknown Orchist)';
                const nick = nicknamesByTargetId.get(s.viewerContactId);
                const reciprocalShare = byOwnerContactId.get(s.viewerContactId);
                const isActive = !s.revokedAt;
                const isStale =
                  isActive &&
                  currentEmail &&
                  s.sharedEmail.toLowerCase() !== currentEmail.toLowerCase();
                return (
                  <li
                    key={s.id}
                    className={`border border-ink/15 px-4 py-3 space-y-1 ${isActive ? '' : 'opacity-60'}`}
                  >
                    <p className={`text-sm truncate ${isPending ? 'font-mono' : 'display'}`}>{display}</p>
                    {isPending && (
                      <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/40">
                        pending acknowledgment
                      </p>
                    )}
                    {nick && (
                      <p className="text-xs text-accent italic font-display">
                        you call them: <span className="text-ink not-italic">{nick.nickname}</span>
                      </p>
                    )}
                    {reciprocalShare && (
                      <p className="font-mono text-xs text-ink/55">
                        <span className="font-display italic text-ink/45">They share with you:</span>{' '}
                        {reciprocalShare.sharedEmail}
                      </p>
                    )}
                    <p className="font-mono text-xs text-ink/65">
                      <span className="font-display italic text-ink/45">They see from you:</span>{' '}
                      {s.sharedEmail}
                      {isStale && (
                        <span className="ml-2 text-accent italic font-display tracking-wide">
                          (older anchor)
                        </span>
                      )}
                      {!isActive && (
                        <span className="ml-2 text-ink/40 italic font-display tracking-wide">
                          (hidden)
                        </span>
                      )}
                    </p>
                    <p className="text-[0.65rem] text-ink/40 italic">
                      Shared {utcToLocalDateString(s.sharedAt, viewerTz) ?? '—'}
                    </p>
                    <div className="flex gap-3 text-xs pt-1 items-center">
                      {/* Toggle: hide if active, show if hidden */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isActive}
                          disabled={actionState === 'busy'}
                          onChange={() => handleToggle(s.viewerContactId, isActive)}
                          className="accent-ink"
                        />
                        <span className="italic font-display text-ink/65">
                          {isActive ? 'Showing' : 'Hidden'}
                        </span>
                      </label>
                      {isStale && (
                        <>
                          <span className="text-ink/25">·</span>
                          <button
                            type="button"
                            onClick={() => handleReshare(s.viewerContactId)}
                            disabled={actionState === 'busy'}
                            className="italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/75 hover:text-ink transition-colors disabled:opacity-50"
                          >
                            Re-share with current
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
